import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  Download,
  FileText,
  Loader2,
  Mail,
} from "lucide-react";
import { useToast } from "../Context/ToastContext";
import { useEmployees } from "../../hooks/useEmployees";
import { formatEmployeeLabel } from "../../components/common/employeeUtils";
import DatePickerField from "../../components/common/DatePickerField";
import TdsFeedbackBanner from "./components/TdsFeedbackBanner";
import TdsField from "./components/TdsField";
import TdsFormulaCard from "./components/TdsFormulaCard";
import TdsStatusBadge from "./components/TdsStatusBadge";
import TdsReportsWelcome from "./components/TdsReportsWelcome";
import TdsReportTypeCards from "./components/TdsReportTypeCards";
import TdsReportsFilters from "./components/TdsReportsFilters";
import TdsReportsActionBar from "./components/TdsReportsActionBar";
import TdsReportsSummary from "./components/TdsReportsSummary";
import TdsReportsDataGrid, { currencyCell } from "./components/TdsReportsDataGrid";
import TdsReportsExportDrawer from "./components/TdsReportsExportDrawer";
import TdsReportsGuidePanel from "./components/TdsReportsGuidePanel";
import TdsReportsHeader from "./components/TdsReportsHeader";
import TdsReportsProgress from "./components/TdsReportsProgress";
import { QUARTERS, formatINR } from "./tdsConstants";
import { loadTdsConfig, saveTdsConfig, getSelectedOrgRegime } from "./tdsStorage";
import {
  downloadForm16,
  fetchEmployeeTaxRegister,
  fetchForm16FinancialYears,
  parseTdsApiError,
} from "./tdsApi";
import { GENERATION_STEPS, MONTH_OPTIONS, TDS_REPORT_TYPES } from "./tdsReportsConfig";
import {
  buildDeductionRows,
  buildProjectionRows,
  exportDeductionRegisterCsv,
  exportProjectionCsv,
  filterRegisterRows,
  groupByField,
} from "./tdsReportsUtils";
import "./tds-reports.css";

function currentPayrollMonth() {
  const map = [9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8];
  return MONTH_OPTIONS[map[new Date().getMonth()]];
}

function regimeDisplay(regime) {
  if (regime === "old") return "Old Regime";
  if (regime === "new") return "New Regime";
  return regime || "—";
}

const GRID_REPORT_IDS = new Set([
  "monthly-register",
  "tax-computation",
  "reconciliation",
  "branch-wise",
  "department-wise",
  "cost-center",
  "tax-ledger",
  "annual-projection",
]);

function getReportMeta(reportId) {
  return TDS_REPORT_TYPES.find((r) => r.id === reportId);
}

export default function TdsReportsCompliance() {
  const toast = useToast();
  const reportRef = useRef(null);
  const { employees, loading: employeesLoading } = useEmployees();
  const [phase, setPhase] = useState("welcome");
  const [selectedReport, setSelectedReport] = useState(null);
  const [config, setConfig] = useState(() => loadTdsConfig());
  const [filters, setFilters] = useState({
    payrollMonth: currentPayrollMonth(),
    department: "",
    branch: "",
    costCenter: "",
    employeeType: "",
    payrollStatus: "",
    employeeId: "",
    regime: "",
    search: "",
  });
  const [selectedEmp, setSelectedEmp] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [fyOptions, setFyOptions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const [registerRows, setRegisterRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [guideMobileOpen, setGuideMobileOpen] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);

  const orgRegime = getSelectedOrgRegime();
  const regimeLabel = regimeDisplay(orgRegime);
  const companyName = config.deductor?.deductorName || "Organization";

  useEffect(() => {
    fetchForm16FinancialYears()
      .then((data) => {
        setFinancialYear(data.current || "");
        setFyOptions(data.options || [data.current].filter(Boolean));
      })
      .catch(() => {
        const y = new Date().getFullYear();
        const start = new Date().getMonth() >= 3 ? y : y - 1;
        const label = `${start}-${String(start + 1).slice(-2)}`;
        setFinancialYear(label);
        setFyOptions([label]);
      });
  }, []);

  const employeeOptions = useMemo(
    () =>
      (employees || []).map((emp) => ({
        id: emp.employee_id,
        name: formatEmployeeLabel(emp),
        code: emp.employee_code || "",
      })),
    [employees]
  );

  const departments = useMemo(() => {
    const set = new Set();
    registerRows.forEach((r) => set.add(r.department || "Unassigned"));
    return [...set].sort();
  }, [registerRows]);

  const filteredRegister = useMemo(
    () => filterRegisterRows(registerRows, filters),
    [registerRows, filters]
  );

  const deductionRows = useMemo(() => buildDeductionRows(filteredRegister), [filteredRegister]);
  const projectionRows = useMemo(() => buildProjectionRows(filteredRegister), [filteredRegister]);

  const reportSummary = useMemo(() => {
    if (!hasGenerated || !deductionRows.length) return null;
    const gross = deductionRows.reduce((s, r) => s + (r.grossIncome || 0), 0);
    const taxable = deductionRows.reduce((s, r) => s + (r.taxableIncome || 0), 0);
    const annualTax = deductionRows.reduce((s, r) => s + (r.annualTax || 0), 0);
    const monthlyTds = deductionRows.reduce((s, r) => s + (r.monthlyTds || 0), 0);
    const withTds = deductionRows.filter((r) => (r.monthlyTds || 0) > 0).length;
    const deptSet = new Set(deductionRows.map((r) => r.department || "Unassigned"));
    return {
      rowCount: deductionRows.length,
      withTds,
      gross,
      taxable,
      annualTax,
      monthlyTds,
      deptCount: deptSet.size,
    };
  }, [hasGenerated, deductionRows]);

  const showFeedback = useCallback(
    (msg, type = "success") => {
      setFeedback({ message: msg, type });
      if (type === "error" || type === "warn") toast.error(msg);
      else toast.success(msg);
    },
    [toast]
  );

  const loadRegister = useCallback(async () => {
    setRegisterLoading(true);
    try {
      const data = await fetchEmployeeTaxRegister();
      setRegisterRows(data.employees || []);
      setStats(data.stats || null);
      return data;
    } catch (err) {
      const msg = await parseTdsApiError(err);
      setFeedback({ type: "error", message: msg });
      toast.error(msg);
      setRegisterRows([]);
      setStats(null);
      throw err;
    } finally {
      setRegisterLoading(false);
    }
  }, [toast]);

  const runGeneration = useCallback(async () => {
    if (!selectedReport) {
      showFeedback("Select a report type first.", "warn");
      return;
    }
    setGenerating(true);
    setProgressStep(0);
    try {
      for (let i = 0; i < GENERATION_STEPS.length - 1; i += 1) {
        setProgressStep(i);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 180));
      }
      await loadRegister();
      setProgressStep(GENERATION_STEPS.length - 1);
      setHasGenerated(true);
      setLastGenerated({ at: new Date().toISOString(), by: "Current session" });
      showFeedback("Report generated successfully.");
      reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      setProgressStep(-1);
    } finally {
      setGenerating(false);
      setTimeout(() => setProgressStep(-1), 1200);
    }
  }, [selectedReport, loadRegister, showFeedback]);

  const handleGenerateForm16 = async (all = false) => {
    if (!all && !selectedEmp) {
      showFeedback("Select an employee first.", "warn");
      return;
    }
    setGenerating(true);
    try {
      const filename = await downloadForm16({
        employeeId: all ? null : selectedEmp,
        financialYear,
        signatory: config.form16,
      });
      const target = all
        ? "all employees"
        : employeeOptions.find((e) => e.id === selectedEmp)?.name || "employee";
      showFeedback(`Form 16 downloaded for ${target}: ${filename}`);
      setLastGenerated({ at: new Date().toISOString(), by: "Current session" });
    } catch (err) {
      const msg = await parseTdsApiError(err);
      showFeedback(msg, "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleExportRegister = () => {
    if (!deductionRows.length) {
      showFeedback("No employee tax data to export.", "warn");
      return;
    }
    exportDeductionRegisterCsv(deductionRows);
    showFeedback("TDS deduction register exported.");
  };

  const handleExportProjection = () => {
    if (!projectionRows.length) {
      showFeedback("No projection data to export.", "warn");
      return;
    }
    exportProjectionCsv(projectionRows);
    showFeedback("Tax projection exported.");
  };

  const handleExport = () => {
    if (!selectedReport) {
      showFeedback("Select a report type first.", "warn");
      return;
    }
    setExportOpen(true);
  };

  const handlePrint = () => {
    if (!hasGenerated && selectedReport !== "form16" && selectedReport !== "quarterly-return") {
      showFeedback("Generate a report before printing.", "warn");
      return;
    }
    window.print();
  };

  const handleReset = () => {
    setFilters({
      payrollMonth: currentPayrollMonth(),
      department: "",
      branch: "",
      costCenter: "",
      employeeType: "",
      payrollStatus: "",
      employeeId: "",
      regime: "",
      search: "",
    });
    setSelectedEmp("");
    setHasGenerated(false);
    setRegisterRows([]);
    setStats(null);
    setLastGenerated(null);
    showFeedback("Filters and report data reset.");
  };

  const updateQuarter = (quarterId, field, value) => {
    const next = {
      ...config,
      quarterlyReturns: config.quarterlyReturns.map((q) =>
        q.quarter === quarterId ? { ...q, [field]: value } : q
      ),
    };
    setConfig(next);
    saveTdsConfig(next);
    if (field === "status" && value === "Filed") {
      showFeedback(`${quarterId} quarterly return marked as Filed and saved.`);
    }
  };

  const getReportStatus = (report) => {
    if (report.statusKey === "quarterly") {
      const filed = config.quarterlyReturns.filter((q) => q.status === "Filed").length;
      return { label: `${filed}/4 Filed`, tone: filed === 4 ? "success" : "warn" };
    }
    if (report.statusKey === "form16") {
      return { label: financialYear ? `FY ${financialYear}` : "Ready", tone: "info" };
    }
    if (hasGenerated && registerRows.length) {
      return { label: `${registerRows.length} rows`, tone: "success" };
    }
    return { label: "Not generated", tone: "muted" };
  };

  const gridConfig = useMemo(() => {
    if (!selectedReport || !GRID_REPORT_IDS.has(selectedReport)) return null;

    const empCol = {
      key: "name",
      label: "Employee",
      render: (row) => (
        <div>
          <div className="font-semibold">{row.name}</div>
          {row.code ? <div className="text-xs text-slate-500">{row.code}</div> : null}
        </div>
      ),
    };

    switch (selectedReport) {
      case "monthly-register":
      case "reconciliation":
        return {
          title: selectedReport === "reconciliation" ? "TDS Reconciliation" : "Monthly TDS Register",
          subtitle:
            selectedReport === "reconciliation"
              ? "Payroll deductions view — use Compliance Center for challan matching."
              : `Month-wise TDS from live employee data · ${filters.payrollMonth}`,
          columns: [
            empCol,
            { key: "taxableIncome", label: "Taxable Income", numeric: true, render: (r) => currencyCell(r.taxableIncome) },
            { key: "annualTax", label: "Annual Tax", numeric: true, render: (r) => currencyCell(r.annualTax) },
            { key: "monthlyTds", label: "Monthly TDS", numeric: true, render: (r) => currencyCell(r.monthlyTds) },
          ],
          rows: deductionRows,
          totals: {
            taxableIncome: formatINR(deductionRows.reduce((s, r) => s + r.taxableIncome, 0)),
            annualTax: formatINR(deductionRows.reduce((s, r) => s + r.annualTax, 0)),
            monthlyTds: formatINR(deductionRows.reduce((s, r) => s + r.monthlyTds, 0)),
          },
          onExport: handleExportRegister,
        };
      case "tax-computation":
        return {
          title: "Employee Tax Computation",
          subtitle: "Annual tax computation with exemptions per employee",
          columns: [
            empCol,
            { key: "grossIncome", label: "Gross Income", numeric: true, render: (r) => currencyCell(r.grossIncome) },
            { key: "taxableIncome", label: "Taxable Income", numeric: true, render: (r) => currencyCell(r.taxableIncome) },
            { key: "annualTax", label: "Annual Tax", numeric: true, render: (r) => currencyCell(r.annualTax) },
            { key: "monthlyTds", label: "Monthly TDS", numeric: true, render: (r) => currencyCell(r.monthlyTds) },
          ],
          rows: deductionRows,
          totals: {
            grossIncome: formatINR(deductionRows.reduce((s, r) => s + r.grossIncome, 0)),
            taxableIncome: formatINR(deductionRows.reduce((s, r) => s + r.taxableIncome, 0)),
            annualTax: formatINR(deductionRows.reduce((s, r) => s + r.annualTax, 0)),
            monthlyTds: formatINR(deductionRows.reduce((s, r) => s + r.monthlyTds, 0)),
          },
          onExport: handleExportRegister,
        };
      case "annual-projection":
        return {
          title: "Annual Projection",
          subtitle: "Projected income, exemptions, and tax liability",
          columns: [
            empCol,
            { key: "projectedIncome", label: "Projected Income", numeric: true, render: (r) => currencyCell(r.projectedIncome) },
            { key: "approvedExemptions", label: "Exemptions", numeric: true, render: (r) => currencyCell(r.approvedExemptions) },
            { key: "taxableIncome", label: "Taxable Income", numeric: true, render: (r) => currencyCell(r.taxableIncome) },
            { key: "taxLiability", label: "Tax Liability", numeric: true, render: (r) => currencyCell(r.taxLiability) },
          ],
          rows: projectionRows,
          totals: {
            projectedIncome: formatINR(projectionRows.reduce((s, r) => s + r.projectedIncome, 0)),
            approvedExemptions: formatINR(projectionRows.reduce((s, r) => s + r.approvedExemptions, 0)),
            taxableIncome: formatINR(projectionRows.reduce((s, r) => s + r.taxableIncome, 0)),
            taxLiability: formatINR(projectionRows.reduce((s, r) => s + r.taxLiability, 0)),
          },
          onExport: handleExportProjection,
        };
      case "tax-ledger":
        return {
          title: "Employee Tax Ledger",
          subtitle: "Running view of projected tax and monthly TDS",
          columns: [
            empCol,
            { key: "taxableIncome", label: "Taxable Income", numeric: true, render: (r) => currencyCell(r.taxableIncome) },
            { key: "taxLiability", label: "Tax Liability", numeric: true, render: (r) => currencyCell(r.taxLiability) },
            { key: "monthlyTds", label: "Monthly TDS", numeric: true, render: (r) => currencyCell(r.monthlyTds) },
          ],
          rows: projectionRows,
          totals: {
            taxableIncome: formatINR(projectionRows.reduce((s, r) => s + r.taxableIncome, 0)),
            taxLiability: formatINR(projectionRows.reduce((s, r) => s + r.taxLiability, 0)),
            monthlyTds: formatINR(projectionRows.reduce((s, r) => s + r.monthlyTds, 0)),
          },
          onExport: handleExportProjection,
        };
      case "department-wise": {
        const grouped = groupByField(deductionRows, "department");
        return {
          title: "Department-wise Report",
          subtitle: "TDS summary grouped by department",
          columns: [
            { key: "label", label: "Department" },
            { key: "count", label: "Employees", numeric: true },
            { key: "taxable", label: "Taxable", numeric: true, render: (r) => currencyCell(r.taxable) },
            { key: "tax", label: "Annual Tax", numeric: true, render: (r) => currencyCell(r.tax) },
            { key: "tds", label: "Monthly TDS", numeric: true, render: (r) => currencyCell(r.tds) },
          ],
          rows: grouped,
          totals: {
            count: grouped.reduce((s, r) => s + r.count, 0),
            taxable: formatINR(grouped.reduce((s, r) => s + r.taxable, 0)),
            tax: formatINR(grouped.reduce((s, r) => s + r.tax, 0)),
            tds: formatINR(grouped.reduce((s, r) => s + r.tds, 0)),
          },
          onExport: handleExportRegister,
        };
      }
      case "branch-wise":
      case "cost-center": {
        const field = selectedReport === "branch-wise" ? "branch" : "costCenter";
        const enriched = deductionRows.map((r) => ({
          ...r,
          branch: r.branch || "Unassigned",
          costCenter: r.costCenter || "Unassigned",
        }));
        const grouped = groupByField(enriched, field);
        return {
          title: getReportMeta(selectedReport)?.title,
          subtitle: "Grouped from employee metadata where available",
          columns: [
            { key: "label", label: selectedReport === "branch-wise" ? "Branch" : "Cost Center" },
            { key: "count", label: "Employees", numeric: true },
            { key: "taxable", label: "Taxable", numeric: true, render: (r) => currencyCell(r.taxable) },
            { key: "tds", label: "Monthly TDS", numeric: true, render: (r) => currencyCell(r.tds) },
          ],
          rows: grouped,
          onExport: handleExportRegister,
        };
      }
      default:
        return null;
    }
  }, [selectedReport, deductionRows, projectionRows, filters.payrollMonth]);

  const hasSignature = Boolean(config.form16.digitalSignature);
  const selectedMeta = getReportMeta(selectedReport);

  return (
    <div className="tdsr-page tds-page-content">
      <TdsFeedbackBanner
        message={feedback?.message}
        type={feedback?.type}
        onClose={() => setFeedback(null)}
      />

      <TdsReportsHeader
        companyName={companyName}
        financialYear={financialYear}
        payrollMonth={filters.payrollMonth}
        regimeLabel={regimeLabel}
        lastGenerated={lastGenerated}
        onGenerate={runGeneration}
        onExport={handleExport}
        onPrint={handlePrint}
        onHelp={() => setGuideMobileOpen(true)}
        generating={generating}
      />

      <div className="tdsr-shell">
        <main className="tdsr-main">
          {phase === "welcome" ? (
            <TdsReportsWelcome onStart={() => setPhase("workspace")} />
          ) : (
            <>
              <TdsReportTypeCards
                selectedId={selectedReport}
                onSelect={setSelectedReport}
                getStatus={getReportStatus}
              />

              {selectedReport && (
                <TdsReportsFilters
                  filters={filters}
                  onChange={setFilters}
                  financialYear={financialYear}
                  onFinancialYearChange={setFinancialYear}
                  fyOptions={fyOptions}
                  departments={departments}
                  employeeOptions={employeeOptions}
                  employeesLoading={employeesLoading}
                  regimeLabel={regimeLabel}
                  fyEditable={selectedReport === "form16"}
                />
              )}

              {selectedReport && (
                <TdsReportsActionBar
                  onGenerate={runGeneration}
                  onPreview={() => reportRef.current?.scrollIntoView({ behavior: "smooth" })}
                  onReset={handleReset}
                  onExport={handleExport}
                  onPrint={handlePrint}
                  onHelp={() => setGuideMobileOpen(true)}
                  generating={generating}
                  disabled={!selectedReport}
                />
              )}

              {progressStep >= 0 && <TdsReportsProgress stepIndex={progressStep} />}

              {hasGenerated && reportSummary && GRID_REPORT_IDS.has(selectedReport) && (
                <TdsReportsSummary stats={stats} summary={reportSummary} />
              )}

              <div ref={reportRef}>
                {selectedReport === "form16" && (
                  <section className="tdsr-report-panel">
                    <div className="tdsr-report-panel__head">
                      <h2 className="tdsr-report-panel__title">
                        <FileText size={16} className="inline text-violet-600 mr-1" />
                        Form 16 Generation
                      </h2>
                      <p className="tdsr-report-panel__sub">
                        Generate employee tax certificate (PDF) from processed payroll
                      </p>
                    </div>
                    <div className="tdsr-special">
                      {!hasSignature && (
                        <div className="tds-alert tds-alert--warn">
                          <AlertTriangle size={16} />
                          Add a digital signature in TDS Configuration to include it on the certificate (optional).
                        </div>
                      )}
                      <TdsField label="Financial Year">
                        <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}>
                          {fyOptions.map((fy) => (
                            <option key={fy} value={fy}>
                              FY {fy}
                            </option>
                          ))}
                        </select>
                      </TdsField>
                      <TdsField label="Select Employee">
                        <select
                          value={selectedEmp}
                          onChange={(e) => setSelectedEmp(e.target.value)}
                          disabled={employeesLoading}
                        >
                          <option value="">
                            {employeesLoading ? "Loading employees…" : "Choose employee…"}
                          </option>
                          {employeeOptions.map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.name}
                              {e.code ? ` (${e.code})` : ""}
                            </option>
                          ))}
                        </select>
                      </TdsField>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="tds-btn tds-btn--primary"
                          disabled={!selectedEmp || generating || !financialYear}
                          onClick={() => handleGenerateForm16(false)}
                        >
                          {generating ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                          {generating ? "Generating…" : "Generate Form 16"}
                        </button>
                        <button
                          type="button"
                          className="tds-btn tds-btn--secondary"
                          disabled={generating || !financialYear || employeeOptions.length === 0}
                          onClick={() => handleGenerateForm16(true)}
                        >
                          {generating ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                          All Employees
                        </button>
                        <button
                          type="button"
                          className="tds-btn tds-btn--secondary"
                          disabled={!selectedEmp}
                          onClick={() =>
                            showFeedback("Email delivery will be available in a future release.", "warn")
                          }
                        >
                          <Mail size={16} /> Email Form 16
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">
                        PDF includes Part A, Part B, and month-wise TDS from processed payroll runs for the selected FY.
                      </p>
                    </div>
                  </section>
                )}

                {selectedReport === "quarterly-return" && (
                  <section className="tdsr-report-panel">
                    <div className="tdsr-report-panel__head">
                      <h2 className="tdsr-report-panel__title">
                        <Calendar size={16} className="inline text-emerald-600 mr-1" />
                        Quarterly Return Status (Form 24Q)
                      </h2>
                      <p className="tdsr-report-panel__sub">
                        Track quarterly TDS filing with the Income Tax Department
                      </p>
                    </div>
                    <div className="tdsr-special">
                      {config.quarterlyReturns.map((qr) => {
                        const qMeta = QUARTERS.find((q) => q.id === qr.quarter);
                        return (
                          <div key={qr.quarter} className="tdsr-quarter-card">
                            <div className="tdsr-quarter-card__head">
                              <div>
                                <span className="font-bold text-slate-900">{qr.quarter}</span>
                                <span className="text-xs text-slate-500 ml-2">{qMeta?.period}</span>
                              </div>
                              <TdsStatusBadge status={qr.status === "Filed" ? "Filed" : "Pending"} />
                            </div>
                            <div className="tds-grid-2 gap-3">
                              <TdsField label="Status">
                                <select
                                  value={qr.status}
                                  onChange={(e) => updateQuarter(qr.quarter, "status", e.target.value)}
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Filed">Filed</option>
                                </select>
                              </TdsField>
                              <TdsField label="Filed Date">
                                <DatePickerField
                                  hideLabel
                                  value={qr.filedDate}
                                  onChange={(date) => updateQuarter(qr.quarter, "filedDate", date)}
                                />
                              </TdsField>
                              <TdsField label="Acknowledgement Number" className="md:col-span-2">
                                <input
                                  value={qr.acknowledgementNumber}
                                  onChange={(e) =>
                                    updateQuarter(qr.quarter, "acknowledgementNumber", e.target.value)
                                  }
                                  placeholder="Filing reference number"
                                />
                              </TdsField>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {selectedReport && GRID_REPORT_IDS.has(selectedReport) && (
                  <section className="tdsr-report-panel">
                    <div className="tdsr-report-panel__head">
                      <h2 className="tdsr-report-panel__title">{gridConfig?.title || selectedMeta?.title}</h2>
                      <p className="tdsr-report-panel__sub">{gridConfig?.subtitle}</p>
                    </div>
                    <TdsReportsDataGrid
                      columns={gridConfig?.columns || []}
                      rows={hasGenerated ? gridConfig?.rows || [] : []}
                      loading={registerLoading}
                      emptyTitle="No report has been generated yet."
                      emptyHint="Choose filters above and click Generate Report."
                      onGenerate={runGeneration}
                      totals={hasGenerated ? gridConfig?.totals : null}
                    />
                    {hasGenerated && selectedReport === "monthly-register" && (
                      <div className="px-5 pb-4">
                        <TdsFormulaCard title="Monthly TDS Formula">
                          <span className="tds-formula-highlight">Monthly TDS</span> = Annual Tax Liability ÷ 12
                        </TdsFormulaCard>
                      </div>
                    )}
                    {hasGenerated && selectedReport === "annual-projection" && (
                      <div className="px-5 pb-4">
                        <TdsFormulaCard title="Taxable Income Formula">
                          <span className="tds-formula-highlight">Taxable Income</span> = Projected Income − Approved Exemptions
                        </TdsFormulaCard>
                      </div>
                    )}
                  </section>
                )}
              </div>

              <button
                type="button"
                className="tds-btn tds-btn--secondary tdsr-guide-mobile-trigger"
                onClick={() => setGuideMobileOpen(true)}
              >
                <BookOpen size={16} />
                Open User Guide
              </button>
            </>
          )}
        </main>

        <TdsReportsGuidePanel className="tdsr-guide--desktop" />
      </div>

      {guideMobileOpen && (
        <TdsReportsGuidePanel
          className="tdsr-guide--mobile tdsr-guide--open"
          onMobileClose={() => setGuideMobileOpen(false)}
        />
      )}

      <TdsReportsExportDrawer
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onExcel={() => {
          gridConfig?.onExport?.() || handleExportRegister();
          setExportOpen(false);
        }}
        onCsv={() => {
          gridConfig?.onExport?.() || handleExportRegister();
          setExportOpen(false);
        }}
        onPdf={handlePrint}
        onPrint={handlePrint}
      />
    </div>
  );
}
